import { onBeforeUnmount, onMounted, ref } from 'vue';

interface BottomDialogEventDetail<TAppId extends string = string> {
	isVisible: boolean;
	appId?: TAppId;
}

interface UseBottomDialogOptions<TAppId extends string = string> {
	key: string;
	appId?: TAppId;
	listen?: boolean;
	initialVisible?: boolean;
}

const getEventName = (key: string) => `hm:${key}:visibility`;

export function useBottomDialog<TAppId extends string = string>(options: UseBottomDialogOptions<TAppId>) {
	const isVisible = ref(options.initialVisible ?? false);

	const setVisible = (value: boolean) => {
		isVisible.value = value;
	};

	const show = () => {
		setVisible(true);
	};

	const hide = () => {
		setVisible(false);
	};

	const matchesAppId = (eventAppId?: TAppId) => {
		if (options.appId) {
			return eventAppId === options.appId;
		}

		return !eventAppId;
	};

	const handleVisibilityEvent = (event: Event) => {
		const { detail } = event as CustomEvent<BottomDialogEventDetail<TAppId> | undefined>;
		if (!detail || !matchesAppId(detail.appId)) {
			return;
		}

		setVisible(detail.isVisible);
	};

	const register = () => {
		window.addEventListener(getEventName(options.key), handleVisibilityEvent);
	};

	const unregister = () => {
		window.removeEventListener(getEventName(options.key), handleVisibilityEvent);
	};

	if (options.listen !== false) {
		onMounted(register);
		onBeforeUnmount(unregister);
	}

	const setDialogVisibility = (value: boolean, targetAppId?: TAppId) => {
		if (!import.meta.client) {
			return;
		}

		const resolvedAppId = targetAppId ?? options.appId;
		const detail: BottomDialogEventDetail<TAppId> = {
			isVisible: value,
		};

		if (resolvedAppId) {
			detail.appId = resolvedAppId;
		}

		window.dispatchEvent(new CustomEvent<BottomDialogEventDetail<TAppId>>(getEventName(options.key), { detail }));
	};

	return {
		isVisible,
		setVisible,
		show,
		hide,
		setDialogVisibility,
		register,
		unregister,
	};
}
